/**
 * EventFormPublic — the public, no-login pre-order form (/event/:id).
 * Renders from the event's stored schema (items and/or menu link + custom
 * fields), gates on the live open/closed lifecycle, and lets an attendee submit
 * one order — and come back to VIEW and EDIT it (per-device) before closing.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { subscribeEvent, placeOrder, findOrderByPhone } from './eventsCloud';
import {
  isAcceptingOrders, closedReason, validateOrder, buildOrderPayload,
  getDeviceId, hasOrdered, getLocalOrder, saveLocalOrder, formatDateTime, normalizeUrl,
  orderTotal, formatMoney, diffOrder, orderEditEntry, normalizePhone,
  itemsToQuantities, mergeQuantities,
} from './eventUtils';
import { fieldIsInput } from './fieldTypes';
import FieldRenderer from './components/FieldRenderer';
import ItemPicker from './components/ItemPicker';
import PhoneField from './components/PhoneField';
import { t } from './strings';
import './Events.css';

/** Full-page status card (not-found / closed / thank-you). */
function StateScreen({ accent, icon, title, body, children }) {
  return (
    <div className="evf" style={{ '--ev-accent': accent || '#f59e0b' }}>
      <div className="evf__wrap">
        <div className="evf__state">
          <div className="evf__state-icon">{icon}</div>
          <h1>{title}</h1>
          {body ? <p>{body}</p> : null}
          {children}
        </div>
      </div>
    </div>
  );
}

/** Read-only recap of an order (used on the confirmation + already-ordered screens). */
function OrderRecap({ event, quantities, orderText, settings }) {
  const items = (event.menuItems || [])
    .map((mi) => ({ ...mi, qty: Number(quantities?.[mi.id]) || 0 }))
    .filter((x) => x.qty > 0);
  const subtotal = orderTotal(event, quantities || {});
  if (!items.length && !orderText) return null;
  return (
    <div className="evf__section evf__recap">
      <h2 className="evf__section-title">{t('yourOrder')}</h2>
      {items.map((it) => (
        <div key={it.id} className="evf__recap-row">
          <span>{it.qty}× {it.name}</span>
          {settings.showPrices && Number(it.price) > 0 ? <span>{formatMoney(it.qty * Number(it.price))}</span> : null}
        </div>
      ))}
      {orderText ? <div className="evf__recap-row"><span>{orderText}</span></div> : null}
      {settings.showPrices && subtotal > 0 ? (
        <div className="evf__recap-row evf__recap-total"><span>{t('subtotal')}</span><span>{formatMoney(subtotal)}</span></div>
      ) : null}
    </div>
  );
}

/** Loading skeleton so the form doesn't flash a bare spinner. */
function Skeleton({ accent }) {
  return (
    <div className="evf" style={{ '--ev-accent': accent || '#f59e0b' }}>
      <div className="evf__wrap">
        <div className="evf__banner evf-skel evf-skel--banner" />
        <div className="evf__section"><div className="evf-skel evf-skel--line" /><div className="evf-skel evf-skel--line short" /></div>
        <div className="evf__section"><div className="evf-skel evf-skel--line" /><div className="evf-skel evf-skel--line" /></div>
      </div>
    </div>
  );
}

export default function EventFormPublic() {
  const { id } = useParams();
  const [event, setEvent] = useState(undefined); // undefined=loading, null=missing
  const [quantities, setQuantities] = useState({});
  const [answers, setAnswers] = useState({});
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [orderText, setOrderText] = useState('');
  const [instructions, setInstructions] = useState('');
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null); // recap snapshot after submit
  const [editing, setEditing] = useState(false);          // editing an existing order
  const [orderId, setOrderId] = useState(null);           // existing order doc id (edit)
  const [editDeviceId, setEditDeviceId] = useState(null); // deviceId to preserve on cross-device edit
  const [editPrev, setEditPrev] = useState(null);         // snapshot of the loaded order (for the diff)
  const [dupOrder, setDupOrder] = useState(null);         // existing order found on a fresh submit
  // Cross-device "find my order" lookup
  const [showLookup, setShowLookup] = useState(false);
  const [lookupPhone, setLookupPhone] = useState('');
  const [lookupBusy, setLookupBusy] = useState(false);
  const [lookupError, setLookupError] = useState('');

  useEffect(() => {
    const u = subscribeEvent(id, setEvent, () => setEvent(null));
    return () => u();
  }, [id]);

  const accent = event?.bannerColor || '#f59e0b';
  const settings = event?.settings || {};
  const totalSelected = useMemo(
    () => Object.values(quantities).reduce((s, q) => s + (Number(q) || 0), 0),
    [quantities],
  );
  const subtotal = useMemo(() => (event ? orderTotal(event, quantities) : 0), [event, quantities]);

  const setQty = (itemId, qty) => setQuantities((prev) => ({ ...prev, [itemId]: qty }));
  const setAnswer = (fieldId, v) => setAnswers((prev) => ({ ...prev, [fieldId]: v }));

  // ── Inline validation (Google-Forms style): validate a field on blur, and
  // clear its error the moment the user starts correcting it. ──────────────────
  const validationOrder = () => ({
    respondentName: name, respondentPhone: phone, respondentEmail: email,
    items: (event?.menuItems || []).map((mi) => ({ id: mi.id, qty: quantities[mi.id] || 0 })),
    answers, orderText,
  });
  const revalidate = (key) => {
    const errs = validateOrder(event, validationOrder());
    setErrors((prev) => {
      const next = { ...prev };
      if (errs[key]) next[key] = errs[key]; else delete next[key];
      return next;
    });
  };
  const clearErr = (key) => setErrors((prev) => {
    if (!prev[key]) return prev;
    const next = { ...prev }; delete next[key]; return next;
  });

  /** Prefill the form from a saved order snapshot and switch to edit mode. */
  const loadForEdit = (snap, deviceId) => {
    setName(snap.name || '');
    setPhone(snap.phone || '');
    setEmail(snap.email || '');
    setQuantities(snap.quantities || {});
    setAnswers(snap.answers || {});
    setOrderText(snap.orderText || '');
    setInstructions(snap.instructions || '');
    setOrderId(snap.orderId || null);
    setEditDeviceId(deviceId || getDeviceId());
    setEditPrev({
      quantities: snap.quantities || {}, orderText: snap.orderText || '',
      name: snap.name || '', phone: snap.phone || '', email: snap.email || '', answers: snap.answers || {},
    });
    setErrors({});
    setConfirmation(null);
    setEditing(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  /** Load the device's saved order into the form and switch to edit mode. */
  const startEdit = () => {
    const local = getLocalOrder(id);
    if (local) loadForEdit(local, getDeviceId());
    else { setEditing(true); window.scrollTo({ top: 0, behavior: 'smooth' }); }
  };

  /** Cross-device lookup: find the order by phone, then prefill it for editing. */
  const handleLookup = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!normalizePhone(lookupPhone)) { setLookupError(t('lookupNoPhone')); return; }
    setLookupBusy(true);
    setLookupError('');
    try {
      const order = await findOrderByPhone(id, lookupPhone);
      if (!order) { setLookupError(t('lookupNotFound')); return; }
      const q = {};
      (order.items || []).forEach((it) => { if (it.id) q[it.id] = it.qty; });
      const snap = {
        orderId: order.id, name: order.respondentName || '',
        phone: order.respondentPhone || lookupPhone, email: order.respondentEmail || '',
        quantities: q, answers: order.answers || {}, orderText: order.orderText || '',
        instructions: order.instructions || '', at: Date.now(),
      };
      saveLocalOrder(id, snap);            // remember on THIS device too
      loadForEdit(snap, order.deviceId || null);
      setShowLookup(false);
    } catch (err) {
      if (err && err.code === 'permission-denied') {
        setLookupError(t('lookupUnavailable'));
        // eslint-disable-next-line no-console
        console.warn('[events] Order lookup blocked by rules — deploy firestore.rules (eventOrders `get` + eventOrderClaims).', err);
      } else {
        setLookupError(t('lookupNotFound'));
      }
    } finally {
      setLookupBusy(false);
    }
  };

  /** A prior-state snapshot of a fetched order (for the change diff + recaps). */
  const snapshotOf = (order) => ({
    quantities: itemsToQuantities(order.items), orderText: order.orderText || '',
    name: order.respondentName || '', phone: order.respondentPhone || '',
    email: order.respondentEmail || '', answers: order.answers || {},
  });

  /**
   * Commit the order (create or update). `finalQ`/`finalText` are the resolved
   * contents; `knownOrderId`+`keepDev` target an existing order (preserving its
   * deviceId so the capability rule passes); `added` tags the merge confirmation.
   */
  const writeOrder = async ({ finalQ, finalText, knownOrderId = null, keepDev = null, prev = null, added = false }) => {
    setSubmitting(true);
    try {
      const payload = buildOrderPayload(
        { ...event, id },
        { respondentName: name, respondentPhone: phone, respondentEmail: email, quantities: finalQ, answers, orderText: finalText, instructions },
        keepDev || getDeviceId(),
      );
      const summary = prev ? diffOrder(event, prev, { quantities: finalQ, orderText: finalText, name, phone, email, answers }) : '';
      const editEntry = knownOrderId ? orderEditEntry(name, summary) : null;
      const res = await placeOrder(id, phone, payload, { knownOrderId, keepDeviceId: keepDev, editEntry });
      saveLocalOrder(id, {
        orderId: res.orderId, name, phone, email, quantities: finalQ, answers, orderText: finalText, instructions, at: Date.now(),
      });
      setOrderId(res.orderId);
      setEditing(false);
      setEditPrev(null);
      setDupOrder(null);
      setConfirmation({ updated: res.updated, added, quantities: { ...finalQ }, orderText: (finalText || '').trim() });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (er) {
      setErrors({ submit: er.message || 'Something went wrong. Please try again.' });
      setSubmitting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const items = (event.menuItems || []).map((mi) => ({ id: mi.id, qty: quantities[mi.id] || 0 }));
    const errs = validateOrder(event, {
      respondentName: name, respondentPhone: phone, respondentEmail: email, items, answers, orderText,
    });
    setErrors(errs);
    if (Object.keys(errs).length) {
      // Scroll to (and focus) the first field with an error — Google-Forms style.
      setTimeout(() => {
        const el = document.querySelector('.evf-field.has-error, .evf-err--block');
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          const input = el.querySelector('input, textarea, select');
          if (input) input.focus({ preventScroll: true });
        }
      }, 30);
      return;
    }

    // Fresh order + this phone already has one → surface it and ask before writing.
    if (!orderId && normalizePhone(phone)) {
      setSubmitting(true);
      try {
        const existing = await findOrderByPhone(id, phone);
        if (existing) { setDupOrder(existing); setSubmitting(false); window.scrollTo({ top: 0, behavior: 'smooth' }); return; }
      } catch { /* lookup failed — fall through to a normal create */ }
    }
    await writeOrder({
      finalQ: quantities, finalText: orderText,
      knownOrderId: orderId, keepDev: editDeviceId,
      prev: editPrev || (orderId ? getLocalOrder(id) : null),
    });
  };

  // ── Duplicate-order actions (from the "you already ordered" screen) ─────────
  const addToExisting = () => {
    const merged = mergeQuantities(dupOrder.items, quantities);
    const mergedText = [dupOrder.orderText, orderText].filter((s) => String(s || '').trim()).join(' · ');
    writeOrder({ finalQ: merged, finalText: mergedText, knownOrderId: dupOrder.id, keepDev: dupOrder.deviceId || null, prev: snapshotOf(dupOrder), added: true });
  };
  const replaceExisting = () => {
    writeOrder({ finalQ: quantities, finalText: orderText, knownOrderId: dupOrder.id, keepDev: dupOrder.deviceId || null, prev: snapshotOf(dupOrder) });
  };
  const editExisting = () => {
    const d = dupOrder;
    setDupOrder(null);
    loadForEdit({
      orderId: d.id, name: d.respondentName || name, phone: d.respondentPhone || phone, email: d.respondentEmail || email,
      quantities: itemsToQuantities(d.items), answers: d.answers || {}, orderText: d.orderText || '', instructions: d.instructions || '',
    }, d.deviceId || null);
  };

  const resetForm = () => {
    setQuantities({}); setAnswers({}); setName(''); setPhone(''); setEmail('');
    setOrderText(''); setInstructions(''); setErrors({});
    setSubmitting(false); setConfirmation(null); setEditing(false); setOrderId(null);
    setEditDeviceId(null); setEditPrev(null);
  };

  // ── State screens ──────────────────────────────────────────────────────────
  if (event === undefined) return <Skeleton accent={accent} />;
  if (event === null) return <StateScreen icon="🔍" title={t('notFoundTitle')} body={t('notFoundBody')} />;

  // Confirmation (just submitted or updated) — with an option to edit again.
  if (confirmation) {
    return (
      <div className="evf" style={{ '--ev-accent': accent }}>
        <div className="evf__wrap">
          <div className="evf__state">
            <div className="evf__state-icon">{confirmation.added ? '➕' : confirmation.updated ? '✅' : '🎉'}</div>
            <h1>{confirmation.added ? t('orderAddedTitle') : confirmation.updated ? t('orderUpdatedTitle') : t('thankYouTitle')}</h1>
            <p>{confirmation.added ? t('orderAddedBody') : confirmation.updated ? t('orderUpdatedBody') : t('thankYouBody')}</p>
          </div>
          <OrderRecap event={event} quantities={confirmation.quantities} orderText={confirmation.orderText} settings={settings} />
          <div className="evf__state" style={{ paddingTop: 4, gap: 10 }}>
            {isAcceptingOrders(event) && (
              <button className="evf__btn" onClick={startEdit}>✎ {t('editMyOrder')}</button>
            )}
            {!settings.oneOrderPerDevice && (
              <button className="evf__btn evf__btn--ghost" onClick={resetForm}>{t('orderAgain')}</button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // "You already ordered" screen — this phone already has an order for this event.
  if (dupOrder) {
    const hasAdding = Object.values(quantities).some((n) => Number(n) > 0) || !!String(orderText || '').trim();
    return (
      <div className="evf" style={{ '--ev-accent': accent }}>
        <div className="evf__wrap">
          <div className="evf__state">
            <div className="evf__state-icon">👋</div>
            <h1>{t('dupTitle')}</h1>
            <p>{t('dupBody')}</p>
          </div>
          <div className="evf__section">
            <h2 className="evf__section-title">
              {t('dupExistingLabel')}{dupOrder.respondentName ? ` · ${dupOrder.respondentName}` : ''}
            </h2>
            <OrderRecap event={event} quantities={itemsToQuantities(dupOrder.items)} orderText={dupOrder.orderText} settings={settings} />
          </div>
          {hasAdding && (
            <div className="evf__section">
              <h2 className="evf__section-title">{t('dupAddingLabel')}</h2>
              <OrderRecap event={event} quantities={quantities} orderText={orderText} settings={settings} />
            </div>
          )}
          <div className="evf__state" style={{ paddingTop: 4, gap: 10 }}>
            {hasAdding && <button className="evf__submit" onClick={addToExisting} disabled={submitting}>{t('dupAdd')}</button>}
            <button className="evf__btn" onClick={editExisting} disabled={submitting}>{t('dupEditExisting')}</button>
            {hasAdding && <button className="evf__btn evf__btn--ghost" onClick={replaceExisting} disabled={submitting}>{t('dupReplace')}</button>}
            <button className="evf__btn evf__btn--ghost" onClick={() => setDupOrder(null)} disabled={submitting}>{t('dupBack')}</button>
          </div>
        </div>
      </div>
    );
  }

  // Ordering window is over — no new orders or edits.
  if (!isAcceptingOrders(event)) {
    const reason = closedReason(event);
    const map = {
      not_open: [t('notOpenTitle'), t('notOpenBody'), '🕓'],
      deadline: [t('deadlineTitle'), t('deadlineBody'), '⌛'],
      closed: [t('closedTitle'), t('closedBody'), '🔒'],
    };
    const [title, body, icon] = map[reason] || map.closed;
    return <StateScreen accent={accent} icon={icon} title={title} body={body} />;
  }

  // Already ordered on this device — show it and offer to edit (unless editing now).
  if (settings.oneOrderPerDevice && hasOrdered(id) && !editing) {
    const local = getLocalOrder(id);
    return (
      <div className="evf" style={{ '--ev-accent': accent }}>
        <div className="evf__wrap">
          <div className="evf__state">
            <div className="evf__state-icon">✅</div>
            <h1>{t('alreadyOrderedTitle')}</h1>
            <p>{t('alreadyOrderedBody')}</p>
          </div>
          <OrderRecap event={event} quantities={local?.quantities} orderText={local?.orderText} settings={settings} />
          <div className="evf__state" style={{ paddingTop: 4 }}>
            <button className="evf__btn" onClick={startEdit}>✎ {t('editMyOrder')}</button>
          </div>
        </div>
      </div>
    );
  }

  // ── The order form (new or editing) ─────────────────────────────────────────
  const items = event.menuItems || [];
  const fields = event.fields || [];
  const showBar = items.length > 0;

  return (
    <div className="evf" style={{ '--ev-accent': accent }}>
      <div className="evf__wrap">
        <div className="evf__banner">
          <h1 className="evf__title">{event.title || 'Event pre-order'}</h1>
          {event.description ? <p className="evf__desc">{event.description}</p> : null}
          <div className="evf__chips">
            {event.deadline ? <span className="evf__pill">⌛ {t('deadlineLabel')} {formatDateTime(event.deadline)}</span> : null}
            {settings.payNote ? <span className="evf__pill">💳 {settings.payNote}</span> : null}
          </div>
        </div>

        {!editing && (
          <div className="evf__findrow">
            {showLookup ? (
              <form className="evf__lookup" onSubmit={handleLookup}>
                <div className="evf__lookup-head">
                  <div>
                    <h2 className="evf__lookup-title">🔎 {t('findMyOrderTitle')}</h2>
                    <p className="evf__lookup-body">{t('findMyOrderBody')}</p>
                  </div>
                  <button type="button" className="evf__lookup-x" onClick={() => { setShowLookup(false); setLookupError(''); }} aria-label={t('lookupClose')}>✕</button>
                </div>
                <div className="evf__lookup-row">
                  <PhoneField value={lookupPhone} onChange={setLookupPhone} />
                  <button type="submit" className="evf__btn" disabled={lookupBusy}>
                    {lookupBusy ? t('lookupSearching') : t('lookupFind')}
                  </button>
                </div>
                {lookupError ? <div className="evf__lookup-error">{lookupError}</div> : null}
              </form>
            ) : (
              <button type="button" className="evf__findlink" onClick={() => setShowLookup(true)}>
                🔎 {t('findMyOrder')}
              </button>
            )}
          </div>
        )}

        {editing && (
          <div className="evf__editbar">
            <span>✎ {t('editYourOrder')}</span>
            <button type="button" className="evf__editbar-cancel" onClick={resetForm}>{t('cancelEdit')}</button>
          </div>
        )}

        <form className="evf__form" onSubmit={handleSubmit} noValidate>
          {errors.submit && <div className="evf__error">{errors.submit}</div>}

          {/* Your details */}
          <section className="evf__section">
            <h2 className="evf__section-title">{t('yourDetails')}</h2>
            <div className={`evf-field${errors.respondentName ? ' has-error' : ''}`}>
              <label className="evf-label">{t('name')}{settings.requireName !== false ? <span className="evf-req">*</span> : null}</label>
              <input className="evf-input" value={name} placeholder={t('name')}
                autoComplete="name" maxLength={60} aria-invalid={errors.respondentName ? true : undefined}
                onChange={(e) => { setName(e.target.value); clearErr('respondentName'); }}
                onBlur={() => revalidate('respondentName')} />
              {errors.respondentName ? <span className="evf-err" role="alert">{errors.respondentName}</span> : null}
            </div>
            {settings.requirePhone && (
              <div className={`evf-field${errors.respondentPhone ? ' has-error' : ''}`}>
                <label className="evf-label">{t('phone')}<span className="evf-req">*</span></label>
                <PhoneField value={phone} error={!!errors.respondentPhone}
                  onChange={(v) => { setPhone(v); clearErr('respondentPhone'); }}
                  onBlur={() => revalidate('respondentPhone')} />
                {errors.respondentPhone ? <span className="evf-err" role="alert">{errors.respondentPhone}</span> : null}
              </div>
            )}
            {settings.requireEmail && (
              <div className={`evf-field${errors.respondentEmail ? ' has-error' : ''}`}>
                <label className="evf-label">{t('email')}<span className="evf-req">*</span></label>
                <input className="evf-input" type="email" value={email} placeholder="you@example.com"
                  inputMode="email" autoComplete="email" maxLength={120} aria-invalid={errors.respondentEmail ? true : undefined}
                  onChange={(e) => { setEmail(e.target.value); clearErr('respondentEmail'); }}
                  onBlur={() => revalidate('respondentEmail')} />
                {errors.respondentEmail ? <span className="evf-err" role="alert">{errors.respondentEmail}</span> : null}
              </div>
            )}
          </section>

          {/* Menu — item picker, or link + free-text order */}
          {(items.length > 0 || event.menuLink) && (
            <section className="evf__section">
              <h2 className="evf__section-title">
                {items.length > 0 ? t('chooseItems') : t('yourOrder')}
                {items.length > 0 && totalSelected > 0
                  ? <span className="evf__count">{totalSelected} {t('itemsSelected')}</span> : null}
              </h2>
              {event.menuLink ? (
                <a className="evf__menulink" href={normalizeUrl(event.menuLink)} target="_blank" rel="noopener noreferrer">
                  📋 {t('viewMenu')}
                </a>
              ) : null}
              {items.length > 0 ? (
                <>
                  {errors.items ? <span className="evf-err evf-err--block">{errors.items}</span> : null}
                  <ItemPicker items={items} quantities={quantities} onChange={setQty} showPrices={settings.showPrices} />
                </>
              ) : (
                <div className={`evf-field${errors.orderText ? ' has-error' : ''}`}>
                  <label className="evf-label">{t('yourOrderQ')}<span className="evf-req">*</span></label>
                  <textarea className="evf-input" rows={3} placeholder={t('yourOrderPlaceholder')} maxLength={500}
                    aria-invalid={errors.orderText ? true : undefined}
                    value={orderText} onChange={(e) => { setOrderText(e.target.value); clearErr('orderText'); }}
                    onBlur={() => revalidate('orderText')} />
                  <span className="evf-count">{orderText.length}/500</span>
                  {errors.orderText ? <span className="evf-err" role="alert">{errors.orderText}</span> : null}
                </div>
              )}
            </section>
          )}

          {/* Custom questions */}
          {fields.length > 0 && (
            <section className="evf__section">
              {fields.map((f) => (
                <FieldRenderer
                  key={f.id}
                  field={f}
                  value={answers[f.id]}
                  onChange={(v) => { setAnswer(f.id, v); clearErr(`field_${f.id}`); }}
                  onBlur={() => revalidate(`field_${f.id}`)}
                  error={fieldIsInput(f.type) ? errors[`field_${f.id}`] : undefined}
                />
              ))}
            </section>
          )}

          {/* Special instructions */}
          {settings.allowInstructions && (
            <section className="evf__section">
              <div className="evf-field">
                <label className="evf-label">{t('specialInstructions')}</label>
                <textarea className="evf-input" rows={2} placeholder={t('instructionsPlaceholder')} maxLength={500}
                  value={instructions} onChange={(e) => setInstructions(e.target.value)} />
                <span className="evf-count">{instructions.length}/500</span>
              </div>
            </section>
          )}

          {/* Sticky order summary + submit */}
          <div className="evf__bar">
            {showBar && (
              <div className="evf__bar-info">
                <span className="evf__bar-count">{totalSelected} {t('itemsSelected')}</span>
                {settings.showPrices && subtotal > 0 ? <span className="evf__bar-total">{formatMoney(subtotal)}</span> : null}
              </div>
            )}
            <button type="submit" className="evf__submit" disabled={submitting}>
              {submitting ? t('submitting')
                : editing ? t('updateOrder')
                : settings.showPrices && subtotal > 0 ? `${t('submit')} · ${formatMoney(subtotal)}`
                : t('submit')}
            </button>
          </div>
        </form>

        <p className="evf__powered">{t('poweredBy')}</p>
      </div>
    </div>
  );
}
